import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Lock, Search, UserMinus, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { cn } from '../ui/utils';
import { useData } from '../../context/DataContext';
import { learningApi } from '../../utils/learning-api';
import { initials } from '../../utils/learning';
import type { AssignmentStatus } from '../../types/learning.types';

/**
 * Add / remove people on one course assignment (LEARNING.md §7.3, §12.4).
 *
 * Shape mirrors `components/AddUsersToPolicyModal.tsx` — same search + checkbox list + footer
 * — but the data is learning's, not policies':
 *  · add    → active, non-archived people who do not already have the course;
 *  · remove → the course's current rows, straight from the assignment detail payload.
 *
 * Remove mode inherits the register's rule: a row that has been opened is a training record.
 * Those candidates render locked with an amber note and cannot be selected — the server
 * reports them as `locked` rather than deleting them silently.
 */

interface AssignmentPeopleModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    courseId: number;
    courseTitle: string;
    mode: 'add' | 'remove';
    /** Learner ids already on the course — excluded from the add list. */
    alreadyAssignedIds: number[];
    onChanged: () => void;
}

interface Candidate {
    id: number;
    name: string;
    jobRole: string | null;
    department: string | null;
    status: AssignmentStatus | null;
    locked: boolean;
}

export const AssignmentPeopleModal: React.FC<AssignmentPeopleModalProps> = ({
    open,
    onOpenChange,
    courseId,
    courseTitle,
    mode,
    alreadyAssignedIds,
    onChanged,
}) => {
    const { users } = useData();

    const [assignedRows, setAssignedRows] = useState<Candidate[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [search, setSearch] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const isRemove = mode === 'remove';

    /** Remove mode needs each learner's status, which only the detail payload carries. */
    useEffect(() => {
        if (!open || !isRemove) return;
        let cancelled = false;

        setSelectedIds([]);
        setSearch('');
        setLoading(true);

        learningApi.assignments
            .detail(courseId)
            .then((detail) => {
                if (cancelled) return;
                setAssignedRows(
                    detail.rows.map((row) => ({
                        id: row.userId,
                        name: row.userName,
                        jobRole: row.userJobRole,
                        department: row.userDepartmentName ?? row.userDepartment,
                        status: row.status,
                        locked: row.status !== 'Assigned',
                    })),
                );
            })
            .catch((error) => console.error('Load assigned people error:', error))
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, isRemove, courseId]);

    useEffect(() => {
        if (!open) return;
        setSelectedIds([]);
        setSearch('');
    }, [open, mode]);

    /** Same source as AddUsersToPolicyModal: DataContext's `users` (GET /api/users). */
    const candidates = useMemo<Candidate[]>(() => {
        if (isRemove) return assignedRows;

        return users
            .filter(
                (user) =>
                    user.isActive &&
                    !user.archived &&
                    !alreadyAssignedIds.includes(Number(user.id)),
            )
            .map((user) => ({
                id: Number(user.id),
                name: user.name,
                jobRole: user.jobRole ?? null,
                department:
                    typeof user.department === 'string'
                        ? user.department
                        : user.department?.departmentName ?? null,
                status: null,
                locked: false,
            }));
    }, [isRemove, assignedRows, users, alreadyAssignedIds]);

    const filteredCandidates = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return candidates;
        return candidates.filter(
            (candidate) =>
                candidate.name.toLowerCase().includes(term) ||
                (candidate.department ?? '').toLowerCase().includes(term) ||
                (candidate.jobRole ?? '').toLowerCase().includes(term),
        );
    }, [candidates, search]);

    const selectable = filteredCandidates.filter((candidate) => !candidate.locked);

    const toggle = (candidate: Candidate) => {
        if (candidate.locked) return;
        setSelectedIds((previous) =>
            previous.includes(candidate.id)
                ? previous.filter((id) => id !== candidate.id)
                : [...previous, candidate.id],
        );
    };

    const handleSubmit = async () => {
        if (selectedIds.length === 0) {
            toast.error(
                isRemove ? 'Pick at least one person to remove.' : 'Pick at least one person to add.',
            );
            return;
        }

        try {
            setSubmitting(true);
            const result = await learningApi.assignments.setPeople(courseId, mode, selectedIds);
            if (isRemove) {
                const lockedNote = result.locked
                    ? `, ${result.locked} already started and left in place`
                    : '';
                toast.success(`Removed ${result.removed ?? 0} learner${result.removed === 1 ? '' : 's'}${lockedNote}`);
            } else {
                const skippedNote = result.skipped
                    ? `, ${result.skipped} already assigned`
                    : '';
                toast.success(`Added ${result.created ?? 0} learner${result.created === 1 ? '' : 's'}${skippedNote}`);
            }
            onChanged();
            onOpenChange(false);
        } catch (error) {
            console.error('Update assignment people error:', error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleOpenChange = (next: boolean) => {
        if (submitting) return;
        onOpenChange(next);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-h-[85vh] overflow-hidden sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {isRemove ? <UserMinus size={20} /> : <UserPlus size={20} />}
                        {isRemove ? 'Remove people from this assignment' : 'Add people to this assignment'}
                    </DialogTitle>
                    <DialogDescription asChild>
                        <div className="space-y-1">
                            <div className="font-medium text-foreground">{courseTitle}</div>
                            <p className="text-sm">
                                {isRemove
                                    ? 'Only assigned learners are shown. A row that has been started is locked.'
                                    : 'Only active people who do not already have this course are shown.'}
                            </p>
                        </div>
                    </DialogDescription>
                </DialogHeader>

                <div className="flex min-h-0 flex-col gap-3">
                    <div className="relative">
                        <Search size={14} className="absolute left-2.5 top-3 text-muted-foreground" />
                        <Input
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="Search people by name, job role or department..."
                            className="pl-8"
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <Checkbox
                            checked={
                                selectable.length > 0 &&
                                selectable.every((candidate) => selectedIds.includes(candidate.id))
                            }
                            onCheckedChange={() =>
                                setSelectedIds(
                                    selectable.length > 0 &&
                                        selectable.every((candidate) =>
                                            selectedIds.includes(candidate.id),
                                        )
                                        ? []
                                        : selectable.map((candidate) => candidate.id),
                                )
                            }
                            disabled={selectable.length === 0}
                        />
                        <span className="text-sm text-muted-foreground">Select all</span>
                    </div>

                    <div className="max-h-[46vh] flex-1 overflow-y-auto rounded-md border border-border">
                        {loading ? (
                            <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
                                <Loader2 size={16} className="animate-spin" />
                                Loading learners...
                            </div>
                        ) : filteredCandidates.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-10 text-center">
                                <p className="text-sm font-medium">Nobody to show</p>
                                <p className="text-sm text-muted-foreground">
                                    {search.trim()
                                        ? 'No one matches your current search.'
                                        : isRemove
                                            ? 'Nobody is assigned to this course yet.'
                                            : 'Everyone active already has this course.'}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-1 p-2">
                                {filteredCandidates.map((candidate) => {
                                    const selected = selectedIds.includes(candidate.id);
                                    return (
                                        <div
                                            key={candidate.id}
                                            role="button"
                                            tabIndex={candidate.locked ? -1 : 0}
                                            aria-disabled={candidate.locked}
                                            onClick={() => toggle(candidate)}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    toggle(candidate);
                                                }
                                            }}
                                            className={cn(
                                                'flex w-full items-center gap-3 rounded-md p-2 text-left transition-colors',
                                                candidate.locked
                                                    ? 'cursor-not-allowed bg-muted/30 opacity-70'
                                                    : selected
                                                        ? 'border border-primary/30 bg-primary/5'
                                                        : 'cursor-pointer hover:bg-muted/50',
                                            )}
                                        >
                                            <Checkbox
                                                checked={selected}
                                                disabled={candidate.locked}
                                                tabIndex={-1}
                                                className="pointer-events-none"
                                            />
                                            <Avatar className="h-8 w-8">
                                                <AvatarFallback className="text-[11px] font-semibold">
                                                    {initials(candidate.name)}
                                                </AvatarFallback>
                                            </Avatar>
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate text-sm font-medium">
                                                    {candidate.name}
                                                </span>
                                                <span className="block truncate text-xs text-muted-foreground">
                                                    {candidate.jobRole ?? '–'} · {candidate.department ?? '–'}
                                                </span>
                                            </span>
                                            {isRemove && candidate.status && (
                                                <span className="shrink-0 text-xs text-muted-foreground">
                                                    {candidate.status}
                                                </span>
                                            )}
                                            {candidate.locked && (
                                                <span className="flex shrink-0 items-center gap-1 text-xs text-amber-600">
                                                    <Lock size={12} />
                                                    Started — cannot be removed
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                <DialogFooter className="items-center sm:justify-between">
                    <span className="text-xs text-muted-foreground">
                        {selectedIds.length} selected
                    </span>
                    <div className="flex flex-col-reverse gap-2 sm:flex-row">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => handleOpenChange(false)}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={handleSubmit}
                            disabled={submitting || selectedIds.length === 0}
                        >
                            {submitting && <Loader2 size={16} className="animate-spin" />}
                            {isRemove ? 'Remove selected' : 'Add selected'}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default AssignmentPeopleModal;
